/** /tours/:id/:tab? — the tour record: one tour, read and edited in place on the booking /
 *  order record structure (plan §1, D1–D3, D10).
 *
 *  - **Tabs** (URL): Tour (the sections — the editable surface) · Itinerary (Line set /
 *    Floorplans by trip type) · History (console_audit_view). A tour's Itinerary tab is the
 *    itinerary workspace (outline + inspector) on the same draft; a bus trip's line set is
 *    on the draft too.
 *  - **Identity strip** (every tab) and, on the Tour tab, the **launch band**: six nodes,
 *    keys `1`–`6`, each to its block of Launch & mapping (or Basics' Chat & app).
 *  - **Related row**: the grids that take the tour, and the chat-channel and media
 *    drawers with their counts.
 *  - **Old slugs** redirect: `overview` and `settings` → `tour#basics`, `content` →
 *    `tour#content`, `identifiers` → `tour#mapping`, `tag-categories` →
 *    `tour#tag-categories`, `departures` → `tour#departures`, `pre-trip-items` →
 *    `tour#pre-trip-items`, `pre-check-in` → `tour#pre-check-in`, `structure` →
 *    `itinerary`.
 *  - **One draft, one ledger, one save**: the draft and the dock are PAGE-level — a tab
 *    switch keeps both, and only leaving the tour is guarded (`/tours/<id>`). The
 *    itinerary's rules run for the whole page (`ItineraryRules`), so an invalid itinerary
 *    keeps Save off — with the reason — on any tab.
 *  - **View first, `E` to edit** (or `?edit=1`): FieldSlots swap their controls in place.
 *    ⌘S save, ⌘Z / ⇧⌘Z undo / redo, Esc Done. No console_tours_manage, no owning operator,
 *    or a deleted tour → view only.
 *  - **The dock** (380 px, from 1100 px of content; D6): **Preview | Changes N** on the
 *    Tour and Itinerary tabs, in view mode too (Preview) — pressing E moves nothing; the
 *    first change switches it to Changes. Save and ⌘S stay at its foot under both
 *    segments. Below 1100 px the Changes ledger is the bottom sheet (edit mode) and the
 *    header's **Preview** opens the phone in a drawer. One preview state for the page
 *    (`TourPreviewProvider`): it renders on request only and follows the Itinerary tab's
 *    selected day. History: no Preview; the ledger docks in edit mode only.
 *  - A draft restored from sessionStorage (`console:draft:tour:<id>`) — or kept after
 *    leaving edit mode — is announced in view mode too.
 *  - Save is one PATCH (`useTourApply`): a 409 refetches and names the drifted fields
 *    (Keep mine / Take theirs) or, when none drifted, resends once.
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Alert, Box, Button } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { Navigate, Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as toursApi from '@/api/tours';
import { TRIP_TYPE, type TourDetail } from '@/api/tours';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { DraftProvider, useDraft, useDraftSummary, useDraftUnsavedGuard } from '@/features/records/draft/DraftProvider';
import { useRecordShortcuts } from '@/features/records/draft/useRecordShortcuts';
import { RecordEditorProvider, useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { StagedFiles } from '@/features/records/fields/widgets/stagedFiles';
import { LedgerDock, SHEET_BELOW_PX } from '@/features/records/ledger/LedgerDock';
import type { RecordActionGroup } from '@/features/records/page/RecordActionsMenu';
import { RecordEmptyState, RecordSkeleton } from '@/features/records/page/RecordStates';
import {
  APPBAR_PX,
  DOCK_GAP_PX,
  missingStatus,
  prefersReducedMotion,
  RecordPageTabs,
  retryUnlessMissing,
  SCROLLER_PADDING_PX,
  useElementWidth,
  useRecordNav,
  type RecordNav,
} from '@/features/records/page/recordPage';
import { OutlineFocusChannel } from '@/features/records/outline/bridge';
import { OutlineIssues } from '@/features/records/outline/issues';
import { PREVIEW_DOCK_WIDTH, PreviewDock } from '@/features/records/preview/PreviewDock';
import { PreviewDrawer } from '@/features/records/preview/PreviewDrawer';
import { usePermissions } from '@/hooks/usePermissions';
import { toursHref } from '@/utils/gridLinks';
import { formatRelativeTime } from '@/utils/format';
import { formatZoned } from '@/utils/zonedTime';

import { TourRelated } from './components/TourRelated';
import { ItineraryWorkspace } from './itinerary/ItineraryWorkspace';
import { ItineraryRules } from './itinerary/rules';
import { useTourBaseline } from './record/baselines';
import { tourFreshValues } from './record/drift';
import { useTourRecordEditorConfig } from './record/editorConfig';
import { TourSectionPanel } from './record/panels/TourSectionPanel';
import { TourNames } from './record/names';
import {
  anchorIdOfHash,
  blockAnchorId,
  ITINERARY_SECTION,
  sectionOfKey,
  type TourBlockId,
  type TourSectionId,
} from './record/registry';
import { tourManagedBy } from './record/editorConfig';
import { IdentityStrip } from './record/IdentityStrip';
import { READINESS_NODES } from './record/readiness';
import { ChatChannelsRelation } from './record/relations/ChatChannelsRelation';
import { TourMediaRelation } from './record/relations/TourMediaRelation';
import { TourRecordHeader } from './record/TourRecordHeader';
import { TourRecordProvider, useTourRecord, type TourRecordData } from './record/TourRecordContext';
import { fetchTourDetail, tourDetailKey } from './record/useTourApply';
import { useReadiness } from './record/useReadiness';
import { useBandMarks } from './record/views/bandMarks';
import { LaunchBand } from './record/views/LaunchBand';
import { TourTabView } from './record/views/TourTabView';
import { TourPreviewProvider } from './preview/TourPreviewContext';
import { TourPreviewPane } from './preview/TourPreviewPane';
import { TourCreateWizard, type WizardData } from './TourCreateWizard';
import { tourTimestampActions } from './tourLinks';

export type TourTabId = 'tour' | 'itinerary' | 'history';

/** Old slugs whose content now lives elsewhere (deep links, bookmarks): a tab, and the
 *  section or block of the Tour tab (`tour#<anchor>`). */
export const TOUR_SLUG_REDIRECTS: Readonly<Record<string, { tab: TourTabId; anchor?: TourSectionId | TourBlockId }>> = {
  overview: { tab: 'tour', anchor: 'basics' },
  content: { tab: 'tour', anchor: 'content' },
  settings: { tab: 'tour', anchor: 'basics' },
  identifiers: { tab: 'tour', anchor: 'mapping' },
  'tag-categories': { tab: 'tour', anchor: 'tag-categories' },
  departures: { tab: 'tour', anchor: 'departures' },
  'pre-trip-items': { tab: 'tour', anchor: 'pre-trip-items' },
  'pre-check-in': { tab: 'tour', anchor: 'pre-check-in' },
  structure: { tab: 'itinerary' },
};

const TABS_LABELS = { label: 'Tour sections', refreshingLabel: 'Refreshing the tour' };

export function itineraryTabLabel(tripType: number): string {
  if (tripType === TRIP_TYPE.BUS_TRIP) return 'Line set';
  if (tripType === TRIP_TYPE.EXHIBITION) return 'Floorplans';
  return 'Itinerary';
}

function tabsFor(detail: TourDetail, canViewHistory: boolean): { id: TourTabId; label: string }[] {
  return [
    { id: 'tour', label: 'Tour' },
    { id: 'itinerary', label: itineraryTabLabel(detail.trip_type) },
    ...(canViewHistory ? [{ id: 'history' as const, label: 'History' }] : []),
  ];
}

const BACK = (
  <Button component={RouterLink} to="/tours" startIcon={<ArrowBackIcon />} size="small">
    Back to tours
  </Button>
);

export function TourDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const location = useLocation();
  const id = Number(idStr);
  if (!Number.isInteger(id) || id <= 0) {
    return (
      <RecordEmptyState icon={<SearchOffIcon />} title={`Tour #${idStr ?? ''} not found`} action={BACK}>
        That is not a tour id.
      </RecordEmptyState>
    );
  }
  const redirect = tab ? TOUR_SLUG_REDIRECTS[tab] : undefined;
  if (redirect) {
    const path = `/tours/${id}${redirect.tab === 'tour' ? '' : `/${redirect.tab}`}`;
    return <Navigate replace to={`${path}${location.search}${redirect.anchor ? `#${redirect.anchor}` : ''}`} />;
  }
  return <TourRecordPage key={id} id={id} tabParam={tab} />;
}

function TourRecordPage({ id, tabParam }: { id: number; tabParam: string | undefined }) {
  const { hasPerm } = usePermissions();
  const nav = useRecordNav<TourTabId>(`/tours/${id}`, 'tour');
  const [params] = useSearchParams();
  const detailQ = useQuery({
    queryKey: tourDetailKey(id),
    queryFn: () => fetchTourDetail(id),
    retry: retryUnlessMissing,
  });
  const missing = missingStatus(detailQ.error);

  if (missing === 404) {
    return (
      <RecordEmptyState icon={<SearchOffIcon />} title={`Tour #${id} not found`} action={BACK}>
        No tour has this id — it may have been deleted. Search the tours grid by name or external ID instead.
      </RecordEmptyState>
    );
  }
  if (missing === 403) {
    return (
      <RecordEmptyState icon={<LockOutlinedIcon />} title={`You can’t open tour #${id}`} action={BACK}>
        Your console role does not include tours. Ask an Ops admin for the console_tours_view permission.
      </RecordEmptyState>
    );
  }
  const detail = detailQ.data;
  if (!detail) {
    return detailQ.error ? (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void detailQ.refetch()}>
            Retry
          </Button>
        }
      >
        Couldn’t load tour #{id}
        {detailQ.error instanceof Error && detailQ.error.message ? ` — ${detailQ.error.message}` : ''}.
      </Alert>
    ) : (
      <RecordSkeleton label="Loading the tour" nodes={6} />
    );
  }

  const tabs = tabsFor(detail, hasPerm('console_audit_view'));
  const activeTab: TourTabId = tabs.find((t) => t.id === tabParam)?.id ?? 'tour';
  const canEdit = hasPerm('console_tours_manage') && Boolean(detail.tenant) && !detail.is_deleted;

  return (
    <TourRecordLoaded
      id={id}
      detail={detail}
      tabs={tabs}
      activeTab={activeTab}
      canEdit={canEdit}
      editing={canEdit && params.get('edit') === '1'}
      fetching={detailQ.isFetching}
      refetch={() => void detailQ.refetch()}
      nav={nav}
    />
  );
}

interface LoadedProps {
  id: number;
  detail: TourDetail;
  tabs: { id: TourTabId; label: string }[];
  activeTab: TourTabId;
  canEdit: boolean;
  editing: boolean;
  fetching: boolean;
  refetch: () => void;
  nav: RecordNav<TourTabId>;
}

interface PendingFocus {
  rec: string;
  path: string;
}

function TourRecordLoaded(props: LoadedProps) {
  const { id, detail, activeTab, canEdit, nav } = props;
  const baseline = useTourBaseline(detail);
  const [files] = useState(() => new StagedFiles());
  const [names] = useState(() => new TourNames());
  const [itineraryIssues] = useState(() => new OutlineIssues());
  const [itineraryFocus] = useState(() => new OutlineFocusChannel());
  // Every loaded detail adds the names of its rows' categories (picks are kept).
  useMemo(() => names.seed(detail), [names, detail]);
  // A ledger row for a field that is not on screen: to its tab, then focus it once its
  // FieldSlot has mounted.
  const pendingFocus = useRef<PendingFocus | null>(null);
  const tabRef = useRef(activeTab);
  tabRef.current = activeTab;
  const { goTab, setParam } = nav;
  const onFocusMissing = useCallback(
    (rec: string, path: string) => {
      const target: TourTabId = sectionOfKey(path) === 'itinerary' ? 'itinerary' : 'tour';
      // On the Itinerary tab: the workspace selects the field's row and focuses it.
      if (tabRef.current === target) {
        if (target === 'itinerary') itineraryFocus.request(rec, path);
        return;
      }
      pendingFocus.current = { rec, path };
      goTab(target);
    },
    [goTab, itineraryFocus],
  );
  const { hasPerm } = usePermissions();
  const config = useTourRecordEditorConfig({
    detail,
    baseline,
    files,
    canEdit,
    onFocusMissing,
    names,
    canSeeGuests: hasPerm('console_bookings_view'),
  });
  const onExitEdit = useCallback(() => setParam('edit', null), [setParam]);
  const record = useMemo<TourRecordData>(
    () => ({ detail, baseline, files, names, links: { tenantId: detail.tenant?.id }, itineraryIssues, itineraryFocus }),
    [detail, baseline, files, names, itineraryIssues, itineraryFocus],
  );

  return (
    <DraftProvider
      kind="tour"
      recordId={id}
      baselines={baseline.paths}
      baselineTs={detail.updated_ts}
      freshFrom={tourFreshValues}
    >
      <RecordEditorProvider mode={props.editing ? 'edit' : 'view'} config={config} onExitEdit={onExitEdit}>
        <TourRecordProvider value={record}>
          <TourPreviewProvider canDraft={hasPerm('console_tours_manage')}>
            <TourRecordShell {...props} pendingFocus={pendingFocus} />
          </TourPreviewProvider>
        </TourRecordProvider>
      </RecordEditorProvider>
    </DraftProvider>
  );
}

function TourRecordShell({
  id,
  detail,
  tabs,
  activeTab,
  canEdit,
  editing,
  fetching,
  refetch,
  nav,
  pendingFocus,
}: LoadedProps & { pendingFocus: MutableRefObject<PendingFocus | null> }) {
  const editor = useRecordEditor();
  const { baseline, itineraryIssues } = useTourRecord();
  const { restored, discardRestored, dispatch } = useDraft();
  const { count } = useDraftSummary();
  const { hasPerm } = usePermissions();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { prompt } = useDraftUnsavedGuard(`/tours/${id}`);
  const { setParam } = nav;
  const enterEdit = useCallback(() => setParam('edit', '1'), [setParam]);
  const [contentRef, contentWidth] = useElementWidth<HTMLDivElement>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  // Below 1100 px of content there is no dock column: the header's Preview opens a drawer.
  const narrow = contentWidth > 0 && contentWidth < SHEET_BELOW_PX;
  const previewTab = activeTab === 'history' ? null : activeTab;
  const [previewOpen, setPreviewOpen] = useState(false);
  // Closed by the page, not the operator: widening past 1100 px (the dock takes over —
  // its opener is gone with the narrow header, so the focus moves to the dock) or History.
  const [autoClosed, setAutoClosed] = useState(false);
  useEffect(() => {
    if (previewOpen && (!narrow || !previewTab)) {
      setAutoClosed(!narrow);
      setPreviewOpen(false);
    }
  }, [narrow, previewTab, previewOpen]);
  const focusDock = useCallback(() => {
    setAutoClosed(false);
    const slot = document.querySelector<HTMLElement>(`[data-record-page="${id}"] [data-ledger-slot]`);
    const target =
      slot?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]') ?? slot?.querySelector<HTMLElement>('aside') ?? null;
    if (!target) return;
    if (target.tagName === 'ASIDE' && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus();
  }, [id]);

  const deleteMutation = useMutation({
    mutationFn: () => toursApi.remove(id),
    onSuccess: () => {
      snackbar.show('Tour deleted.', 'success');
      void qc.invalidateQueries({ queryKey: ['tours'] });
      navigate('/tours');
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const onTour = activeTab === 'tour' && Boolean(detail.tenant);
  const checks = useReadiness();
  const bandMarks = useBandMarks();
  // The band's height places the ledger's side column under it and clears it when a node
  // scrolls a block into view.
  const bandRef = useRef<HTMLDivElement | null>(null);
  const [bandHeight, setBandHeight] = useState(0);
  useLayoutEffect(() => {
    const el = bandRef.current;
    if (!el) {
      setBandHeight(0);
      return undefined;
    }
    const measure = () => setBandHeight(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onTour]);

  // A ledger row for a field on another tab: focus it once the tab has rendered.
  useEffect(() => {
    const pending = pendingFocus.current;
    if (!pending) return undefined;
    pendingFocus.current = null;
    const raf = requestAnimationFrame(() => editor.focusField(pending.rec, pending.path));
    return () => cancelAnimationFrame(raf);
  }, [activeTab, editor, pendingFocus]);

  // `tour#<section>` / `tour#<block>` (the old slugs land here): scroll it into view.
  const hash = location.hash.slice(1);
  useEffect(() => {
    if (activeTab !== 'tour' || !hash) return;
    const anchor = anchorIdOfHash(hash);
    const panel = anchor ? document.getElementById(anchor) : null;
    if (!panel) return;
    panel.scrollIntoView?.({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    panel.focus({ preventScroll: true });
  }, [activeTab, hash]);

  /** Scroll to a block of the Tour tab and focus it — from another tab, by way of the
   *  `tour#<block>` hash. */
  const goToBlock = useCallback(
    (block: TourBlockId) => {
      if (activeTab !== 'tour') {
        navigate(`${nav.hrefFor('tour')}#${block}`);
        return;
      }
      const el = document.getElementById(blockAnchorId(block));
      if (!el) return;
      el.scrollIntoView?.({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      el.focus({ preventScroll: true });
    },
    [activeTab, navigate, nav],
  );

  useRecordShortcuts(
    {
      ...(canEdit && !editing ? { onEdit: enterEdit } : {}),
      ...(onTour
        ? {
            onStage: (i: number) => {
              const node = READINESS_NODES[i];
              if (node) goToBlock(node);
            },
          }
        : {}),
    },
    { stageCount: READINESS_NODES.length },
  );

  const duplicable = hasPerm('console_tours_manage') && Boolean(detail.tenant) && !detail.is_deleted;
  const actionGroups: RecordActionGroup[] = [
    {
      key: 'tour',
      label: 'Tour',
      items: [
        { key: 'refresh', label: 'Refresh', hint: 'reload from the server', onSelect: refetch },
        ...(duplicable
          ? [
              {
                key: 'duplicate',
                label: 'Duplicate…',
                hint: 'the create wizard',
                ...(detail.trip_type === TRIP_TYPE.EXHIBITION
                  ? { disabled: true, description: 'Exhibitions cannot be created in the console yet' }
                  : {}),
                onSelect: () => setDuplicating(true),
              },
            ]
          : []),
        // A real link (new tab, cmd-clickable), as the catalog records' item.
        ...(detail.dashboard_url
          ? [{ key: 'dashboard', label: 'Open in dashboard', hint: 'new tab', href: detail.dashboard_url }]
          : []),
        ...(hasPerm('console_tours_manage') && !detail.is_deleted
          ? [
              {
                key: 'delete',
                label: 'Delete tour…',
                hint: 'background task',
                ...(count > 0 ? { disabled: true, description: 'Save or discard the open changes first' } : {}),
                onSelect: () => setConfirmDelete(true),
              },
            ]
          : []),
      ],
    },
  ];
  const section = tabs.find((t) => t.id === activeTab)?.label ?? 'Tour';


  return (
    <Box sx={{ '--record-spine-height': `${bandHeight + DOCK_GAP_PX}px` }} data-record-page={id}>
      <TourRecordHeader
        detail={detail}
        section={section}
        canEdit={canEdit}
        editing={editing}
        dirty={count > 0}
        onEdit={enterEdit}
        onDone={editor.requestClose}
        onDiscard={() => dispatch({ type: 'DISCARD_ALL' })}
        actionGroups={actionGroups}
        {...(narrow && previewTab && detail.tenant ? { onPreview: () => setPreviewOpen(true), previewOpen } : {})}
      />
      <TourRelated
        tour={detail}
        extra={
          detail.tenant
            ? [<ChatChannelsRelation key="chat" tour={detail} />, <TourMediaRelation key="media" tour={detail} />]
            : []
        }
      />
      <IdentityStrip detail={detail} onShowIdentifiers={() => goToBlock('mapping')} />

      {!detail.tenant ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This tour has no owning operator, so its catalog editor is unavailable. Delete it from the Actions menu to
          remove this orphaned record.
        </Alert>
      ) : (
        <>
          {!editing && count > 0 && (
            <Alert
              severity="info"
              icon={<EditNoteOutlinedIcon />}
              sx={{ mb: 2 }}
              data-view-draft-banner
              action={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {canEdit && (
                    <Button color="inherit" size="small" onClick={enterEdit}>
                      Continue editing
                    </Button>
                  )}
                  <Button color="inherit" size="small" onClick={restored ? discardRestored : () => dispatch({ type: 'DISCARD_ALL' })}>
                    Discard
                  </Button>
                </Box>
              }
            >
              {restored
                ? `Restored ${count} unsaved ${count === 1 ? 'change' : 'changes'} from ${formatZoned(restored.savedAt, detail.start_timezone, { weekday: false })} — not saved yet.`
                : `${count} unsaved ${count === 1 ? 'change is' : 'changes are'} kept in the editor — not saved yet.`}
            </Alert>
          )}

          <RecordPageTabs tabs={tabs} active={activeTab} hrefFor={nav.hrefFor} fetching={fetching} {...TABS_LABELS} />

          {onTour && (
            <LaunchBand
              ref={bandRef}
              detail={detail}
              checks={checks}
              editing={editing}
              marks={bandMarks}
              managedBy={tourManagedBy(detail)}
              onSelect={goToBlock}
              compactMarks={contentWidth > 0 && contentWidth < SHEET_BELOW_PX}
            />
          )}

          <Box ref={contentRef}>
            <LedgerDock
              open={editing}
              dockInView={activeTab !== 'history'}
              width={PREVIEW_DOCK_WIDTH}
              stickyTop={(onTour ? bandHeight : 0) - SCROLLER_PADDING_PX + DOCK_GAP_PX}
              viewportOffset={APPBAR_PX + SCROLLER_PADDING_PX}
              ledger={(variant) => (
                <PreviewDock
                  variant={variant}
                  editing={editing}
                  canEdit={canEdit}
                  preview={activeTab === 'history' ? null : <TourPreviewPane tab={activeTab} />}
                />
              )}
            >
              {activeTab === 'tour' && <TourTabView width={contentWidth} />}
              {activeTab === 'itinerary' &&
                (detail.trip_type === TRIP_TYPE.BUS_TRIP ? (
                  <Box sx={{ display: 'grid', gap: 2 }}>
                    <TourSectionPanel section={ITINERARY_SECTION} />
                    {!detail.vehicle_route_set && (
                      <Alert severity="warning">This bus trip has no line set, so it has no guest content yet.</Alert>
                    )}
                    <Alert severity="info">
                      Line-set contents (routes, stops, schedules) are edited in the bus-tours area.
                    </Alert>
                  </Box>
                ) : detail.trip_type === TRIP_TYPE.EXHIBITION ? (
                  <Alert severity="info">Exhibitions have no editable floorplans yet.</Alert>
                ) : (
                  <ItineraryWorkspace editing={editing} canEdit={canEdit} width={contentWidth} />
                ))}
              {activeTab === 'history' && (
                <HistoryPanel contentType="demo.group_tour" objectPk={String(id)} onRestored={refetch} />
              )}
            </LedgerDock>
          </Box>
        </>
      )}

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        Last updated{' '}
        {detail.updated_ts ? (
          <ValueActions actions={tourTimestampActions('updated', detail.updated_ts, { tenantId: detail.tenant?.id })} title={detail.updated_ts}>
            {formatRelativeTime(detail.updated_ts)}
          </ValueActions>
        ) : (
          formatRelativeTime(detail.updated_ts)
        )}{' '}
        ·{' '}
        <RouterLink to={toursHref([], detail.tenant?.id)} style={{ color: '#0a1a3f' }}>
          ← Back to tours
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this tour?"
        message={`"${detail.name}" will be deleted in the background${
          detail.user_stats?.total ? `, including ${detail.user_stats.total} booking(s)` : ''
        }. This cannot be undone.`}
        confirmLabel="Delete tour"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
      {detail.tenant && detail.trip_type === TRIP_TYPE.TOUR && detail.itinerary && (
        <ItineraryRules baseline={baseline} issues={itineraryIssues} />
      )}
      {duplicating && detail.tenant && (
        <TourCreateWizard open onClose={() => setDuplicating(false)} prefill={duplicatePrefill(detail)} />
      )}
      {/* Mounted while it may be open or closing — not only while narrow — so a close
          caused by widening still runs its exit, and hands the focus to the dock. */}
      {detail.tenant && (
        <PreviewDrawer
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          restoreFocus={!autoClosed}
          {...(autoClosed ? { onExited: focusDock } : {})}
        >
          <TourPreviewPane tab={previewTab ?? 'tour'} />
        </PreviewDrawer>
      )}
      {prompt}
    </Box>
  );
}

/** The create wizard filled in from a tour (Duplicate): its operator and kind, the tour as
 *  the source, "<name> (copy)", its scheduling (a fixed-date tour's start date), time zone
 *  and own launch policy. */
export function duplicatePrefill(detail: TourDetail): Partial<WizardData> {
  return {
    operator: detail.tenant ? { id: detail.tenant.id, name: detail.tenant.name, code: detail.tenant.code } : null,
    tripType: detail.trip_type,
    mode: 'duplicate',
    source: { id: detail.id, name: detail.name },
    name: `${detail.name} (copy)`,
    timezone: detail.start_timezone ?? '',
    isBespoke: detail.is_bespoke,
    startDate: detail.is_bespoke ? (detail.start_date ?? '') : '',
    launchPolicy: detail.launch_policy ? { id: detail.launch_policy.id, name: detail.launch_policy.name } : null,
  };
}
